import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Fade,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { associateMultiplexPanel, recordMultiplexPanelApproval, saveMultiplexPanel } from 'api/multiplexPanels';
import SlideLinkWithPreview from 'components/atoms/SlideLinkWithPreview';
import { UserIdentity } from 'components/atoms/UserIdentity';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import CardContentEditable from 'components/StudiesDashboard/CardContentEditable';
import { PanelHeaderSummary } from 'components/StudyDashboard/MultiplexPanels/PanelHeaderSummary';
import { PanelSlideDetails } from 'components/StudyDashboard/MultiplexPanels/PanelSlideDetails';
import { MultiplexPanelRevision, MultiplexPanelView } from 'interfaces/multiplexPanels';
import { StainType } from 'interfaces/stainType';
import { UsersById } from 'interfaces/userCredentials';
import {
  compact,
  filter,
  find,
  first,
  flatMap,
  includes,
  isEqual,
  join,
  map,
  some,
  startCase,
  uniq,
  uniqBy,
} from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';
import { buildSlideViewerUrl } from 'utils/helpers';
import { ChannelModality } from 'utils/multiplex/channelModality';
import { invalidateMarkerAssignmentQueries } from 'utils/queryHooks/markerAssignmentQueries';
import {
  areSelectedSlidesApprovable,
  buildPanelSaveRequest,
  getPanelLabel,
  initializePanelChannels,
  PanelChannelDraft,
  validatePanelChannels,
} from './panelForm';
import { RenamePanelDialog } from './RenamePanelDialog';

interface Props {
  panel: MultiplexPanelView;
  panels: MultiplexPanelView[];
  usersById?: UsersById;
  studyId: string;
  labId?: string;
  stains: StainType[];
  displayNames: Record<string, string>;
  panelRevisions?: MultiplexPanelRevision[];
  initialChannels?: PanelChannelDraft[];
  isNewPanelDraft?: boolean;
  onDefineNewPanel?: (channels: PanelChannelDraft[], slideIds: string[]) => void;
  onDiscardDraft?: () => void;
  onSaved?: (revision: MultiplexPanelRevision, requiresReanalysis: boolean) => void;
  initialSlideIds?: string[];
  sourceSlideId?: string;
  expanded: boolean;
  canEdit: boolean;
  refreshing?: boolean;
  onAssociated?: (requiresReanalysis: boolean) => void;
  onExpand: () => void;
  onDirtyChange: (panelId: string, dirty: boolean) => void;
}

const errorMessage = (error: unknown) => {
  const detail = (error as { response?: { body?: { detail?: string | string[] } } })?.response?.body?.detail;
  return Array.isArray(detail)
    ? join(detail, ' ')
    : typeof detail === 'string'
    ? detail
    : 'The request could not be confirmed. Your edits are still here. Refresh panel information before retrying.';
};

export const PanelEditor = ({
  panel,
  panels,
  usersById,
  studyId,
  labId,
  stains,
  displayNames,
  panelRevisions = [],
  initialChannels,
  isNewPanelDraft = false,
  onDefineNewPanel,
  onDiscardDraft,
  onSaved,
  initialSlideIds,
  sourceSlideId,
  expanded,
  canEdit,
  refreshing = false,
  onAssociated,
  onExpand,
  onDirtyChange,
}: Props) => {
  const queryClient = useQueryClient();
  const confirm = useConfirmation();
  const eligibleSlides = uniqBy([...panel.slides, ...(panel.candidateSlides || [])], 'slideId');
  const [baseline, setBaseline] = useState(
    () => initialChannels || initializePanelChannels(panel, stains, sourceSlideId, displayNames)
  );
  const [channels, setChannels] = useState(baseline);
  const [slideSelection, setSelectedSlideIds] = useState(() =>
    map(
      filter(
        initialSlideIds ? eligibleSlides : panel.slides,
        (slide) => slide.match !== 'unmatched' && (!initialSlideIds || includes(initialSlideIds, slide.slideId))
      ),
      'slideId'
    )
  );
  const selectedSlideIds = filter(uniq(slideSelection), (slideId) =>
    Boolean(find(eligibleSlides, (slide) => slide.slideId === slideId && slide.match !== 'unmatched'))
  );
  const [localRevision, setLocalRevision] = useState<number | null>(panel.revision || null);
  const [persistedPanelId, setPersistedPanelId] = useState(panel.panelId);
  const [draftName, setDraftName] = useState(panel.name || '');
  const [appliedSlideIds, setAppliedSlideIds] = useState<string[]>([]);
  const [requestError, setRequestError] = useState<string>();
  const requestErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (requestError) requestErrorRef.current?.focus();
  }, [requestError]);
  const [page, setPage] = useState(0);
  const [slideFilter, setSlideFilter] = useState('all');
  const [splitValues, setSplitValues] = useState<Record<string, string>>({});
  const dirty = !isEqual(channels, baseline);
  const validation = validatePanelChannels(channels, stains, persistedPanelId, panelRevisions);
  const baselineValidation = validatePanelChannels(baseline, stains, persistedPanelId, panelRevisions);
  const analysisChanges = !isEqual(
    map(channels, (channel) => [
      channel.channelIndex,
      channel.identifiers,
      channel.channelModality,
      validation.analysisNames[channel.channelIndex],
    ]),
    map(baseline, (channel) => [
      channel.channelIndex,
      channel.identifiers,
      channel.channelModality,
      baselineValidation.analysisNames[channel.channelIndex],
    ])
  );
  const isApproved =
    panel.saved && panel.slides.length > 0 && panel.slides.every((slide) => slide.approvalStatus === 'approved');
  const activeStains = filter(stains, (stain) => !stain.deletedAt && (stain.canBeMifMarker || stain.dna));
  const selectedSlides = filter(eligibleSlides, (slide) => includes(selectedSlideIds, slide.slideId));
  const associationSlideIds = map(
    filter(
      selectedSlides,
      (slide) => !find(panel.slides, { slideId: slide.slideId }) || slide.approvalStatus === 'diverged'
    ),
    'slideId'
  );

  const refresh = () =>
    Promise.all([
      invalidateMarkerAssignmentQueries(queryClient),
      queryClient.invalidateQueries({ queryKey: ['multiplex_panels'] }),
      queryClient.invalidateQueries({ queryKey: ['study', studyId] }),
    ]);
  const save = useMutation({
    mutationKey: ['multiplex_panel_mutations', studyId],
    mutationFn: () =>
      saveMultiplexPanel(
        studyId,
        buildPanelSaveRequest(
          { ...panel, panelId: persistedPanelId, saved: Boolean(localRevision), name: draftName },
          channels,
          selectedSlideIds
        )
      ),
    onSuccess: async (revision) => {
      const savedChannels = map(revision.channels, ({ analysisName, ...channel }) => ({
        ...channel,
        rnaProbeId: channel.channelModality === 'rna' ? analysisName : undefined,
      }));
      setChannels(savedChannels);
      setBaseline(savedChannels);
      setLocalRevision(revision.revision);
      setPersistedPanelId(revision.panelId);
      setAppliedSlideIds(selectedSlideIds);
      onDirtyChange(panel.panelId, false);
      setRequestError(undefined);
      await refresh();
      onSaved?.(revision, isNewPanelDraft || !localRevision || analysisChanges);
    },
    onError: (error) => setRequestError(errorMessage(error)),
  });
  const approval = useMutation({
    mutationKey: ['multiplex_panel_mutations', studyId],
    mutationFn: (action: 'approved' | 'revoked') =>
      recordMultiplexPanelApproval(studyId, {
        panelId: persistedPanelId,
        revision: localRevision as number,
        slideIds: selectedSlideIds,
        action,
      }),
    onSuccess: async () => {
      setRequestError(undefined);
      await refresh();
    },
    onError: (error) => setRequestError(errorMessage(error)),
  });
  const association = useMutation({
    mutationKey: ['multiplex_panel_mutations', studyId],
    mutationFn: () =>
      associateMultiplexPanel(studyId, {
        panelId: persistedPanelId,
        revision: localRevision as number,
        slideIds: associationSlideIds,
      }),
    onSuccess: async (result) => {
      setRequestError(undefined);
      setAppliedSlideIds(result.membership.slideIds);
      await refresh();
      onAssociated?.(result.requiresReanalysis);
    },
    onError: (error) => setRequestError(errorMessage(error)),
  });
  const busy = save.isLoading || approval.isLoading || association.isLoading || refreshing;
  const canApprove =
    !dirty &&
    Boolean(localRevision) &&
    selectedSlideIds.length > 0 &&
    (areSelectedSlidesApprovable(panel, selectedSlideIds) ||
      selectedSlideIds.every((slideId) => includes(appliedSlideIds, slideId)));
  const selectedApproved =
    selectedSlides.length > 0 && selectedSlides.every((slide) => slide.approvalStatus === 'approved');
  const needsSave = dirty || !localRevision;
  const needsAssociation =
    associationSlideIds.length > 0 || (!selectedSlideIds.length && Boolean(panel.candidateSlides?.length));
  const panelLabel = panel.name || getPanelLabel(persistedPanelId, panels);
  const isDraft = isNewPanelDraft && !localRevision;
  const [isRenaming, setIsRenaming] = useState(false);
  const canRename = canEdit && panel.saved && Boolean(panel.ownedByStudy) && Boolean(localRevision);
  const otherMatchingSlideCount = filter(
    eligibleSlides,
    (slide) => slide.match !== 'unmatched' && slide.slideId !== sourceSlideId
  ).length;
  const applyPanel = async () => {
    if (
      await confirm({
        title: `Apply ${panelLabel} to ${associationSlideIds.length} ${
          associationSlideIds.length === 1 ? 'slide' : 'slides'
        }?`,
        text: 'Only selected slides receive this panel mapping. Moving a slide revokes only its former approval. Panel-dependent analysis may need re-analysis; initial image analysis and historical results are retained.',
        confirmButtonProps: { title: 'Apply panel', color: 'primary', variant: 'contained' },
        cancelButtonProps: { title: 'Cancel' },
      })
    )
      association.mutate();
  };

  const updateChannel = (index: number, patch: Partial<PanelChannelDraft>) => {
    const updated = map(channels, (channel) => (channel.channelIndex === index ? { ...channel, ...patch } : channel));
    setChannels(updated);
    setRequestError(undefined);
    onDirtyChange(panel.panelId, !isEqual(updated, baseline));
  };
  const metadataFilters = [
    { key: 'biopsyType', label: 'Biopsy type', values: uniq(compact(map(eligibleSlides, 'biopsyType'))) },
    {
      key: 'indications',
      label: 'Indication',
      values: uniq(compact(flatMap(eligibleSlides, (slide) => slide.indications || []))),
    },
    {
      key: 'subIndications',
      label: 'Sub-indication',
      values: uniq(compact(flatMap(eligibleSlides, (slide) => slide.subIndications || []))),
    },
  ].filter((split) => split.values.length > 1);
  const filteredSlides = filter(
    eligibleSlides,
    (slide) =>
      (slideFilter === 'all' ||
        (slideFilter === 'diverged' ? slide.approvalStatus === 'diverged' : slide.match === slideFilter)) &&
      metadataFilters.every((split) => {
        const value = splitValues[split.key];
        return (
          !value ||
          !includes(split.values, value) ||
          (split.key === 'biopsyType'
            ? slide.biopsyType === value
            : includes(slide[split.key as 'indications' | 'subIndications'] || [], value))
        );
      })
  );
  const showSlideFilter =
    uniq(map(eligibleSlides, (slide) => (slide.approvalStatus === 'diverged' ? 'diverged' : slide.match))).length > 1;
  const visiblePage = Math.min(page, Math.max(Math.ceil(filteredSlides.length / 10) - 1, 0));
  const selectableFilteredSlides = filter(filteredSlides, (slide) => slide.match !== 'unmatched');

  return (
    <>
      <Fade in appear={isDraft} timeout={400}>
        <Box sx={{ position: 'relative' }}>
          {!localRevision && (
            <Chip
              size="small"
              color="secondary"
              label="New"
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                fontWeight: 700,
                minWidth: 72,
                justifyContent: 'center',
              }}
            />
          )}
          <Accordion
            expanded={isDraft ? true : expanded}
            onChange={isDraft ? undefined : onExpand}
            disableGutters
            sx={isDraft ? { border: 2, borderColor: 'secondary.main' } : undefined}
          >
            <AccordionSummary
              expandIcon={
                isDraft && onDiscardDraft ? (
                  <IconButton
                    size="small"
                    aria-label={`Cancel ${panelLabel}`}
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDiscardDraft();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <ExpandMoreIcon />
                )
              }
              aria-label={isDraft ? panelLabel : undefined}
            >
              <PanelHeaderSummary
                title={
                  isDraft ? (
                    <CardContentEditable
                      disabled={!canEdit || busy}
                      savedValue={draftName}
                      onSave={setDraftName}
                      onEditModeChange={() => undefined}
                      placeholder={panelLabel}
                    />
                  ) : (
                    panelLabel
                  )
                }
                titleAction={
                  canRename && (
                    <IconButton
                      size="small"
                      aria-label={`Rename ${panelLabel}`}
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsRenaming(true);
                      }}
                      onFocus={(event) => event.stopPropagation()}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )
                }
                markerNames={map(
                  filter(channels, (channel) => channel.channelModality !== 'dna'),
                  (channel) =>
                    channel.displayName ||
                    find(stains, { id: channel.stainTypeId })?.displayName ||
                    `Marker ${channel.channelIndex + 1}`
                )}
              >
                <Chip
                  size="small"
                  label={`${panel.slides.length} mapped ${panel.slides.length === 1 ? 'slide' : 'slides'}`}
                />
                {Boolean(panel.candidateSlides?.length) && (
                  <Chip
                    size="small"
                    label={`${panel.candidateSlides?.length} compatible ${
                      panel.candidateSlides?.length === 1 ? 'candidate' : 'candidates'
                    }`}
                  />
                )}
                {Boolean(localRevision) && (
                  <Chip
                    size="small"
                    label={!panel.slides.length ? 'No associated slides' : isApproved ? 'Approved' : 'Needs approval'}
                    color={isApproved ? 'success' : panel.slides.length ? 'warning' : 'default'}
                  />
                )}
              </PanelHeaderSummary>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ minWidth: 0 }}>
                {isDraft && (
                  <Alert severity="info">
                    Saving moves the selected slides here and revokes their former approvals. Panel-dependent analyses
                    need re-analysis; initial image analysis is retained.
                  </Alert>
                )}
                <Typography variant="subtitle2">Panel markers</Typography>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 560 }} aria-label={`Channels for ${panel.panelId}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Channel identifier</TableCell>
                        <TableCell>Modality</TableCell>
                        <TableCell>Marker / RNA probe</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {map(channels, (channel) => {
                        const index = channel.channelIndex;
                        const stain = find(stains, { id: channel.stainTypeId });
                        const issues = validation.issues[index] || [];
                        const source = find(panel.channels, { channelIndex: index });
                        const suggestion = first(source?.suggestions);
                        return (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{join(channel.identifiers.sourceNames, ' / ') || 'Unavailable'}</TableCell>
                            <TableCell>
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                color="primary"
                                value={channel.needsModalityChoice ? null : channel.channelModality}
                                disabled={!canEdit || busy}
                                aria-label={`Type for channel ${index}`}
                                sx={{
                                  '& .MuiToggleButton-root.Mui-selected:not(.Mui-disabled)': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                  },
                                }}
                                onChange={(_event, value: ChannelModality | null) => {
                                  if (value)
                                    updateChannel(index, {
                                      channelModality: value,
                                      needsModalityChoice: false,
                                      displayName:
                                        value === 'rna' ? channel.displayName || stain?.displayName || '' : null,
                                    });
                                }}
                              >
                                <ToggleButton value="dna" disabled={!stain?.dna}>
                                  DNA
                                </ToggleButton>
                                <ToggleButton value="rna">RNA</ToggleButton>
                                <ToggleButton value="mif" disabled={Boolean(stain?.dna)}>
                                  mIF
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              {channel.channelModality === 'rna' ? (
                                <Autocomplete
                                  freeSolo
                                  options={map(activeStains, 'displayName')}
                                  value={channel.displayName || ''}
                                  disabled={!canEdit || busy}
                                  onInputChange={(_event, value, reason) => {
                                    if (reason === 'input' || reason === 'clear')
                                      updateChannel(index, { displayName: value });
                                  }}
                                  onChange={(_event, value) =>
                                    updateChannel(index, {
                                      displayName: value || '',
                                    })
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      label={`RNA display name · channel ${index}`}
                                      helperText="Display-only; editable after analysis"
                                    />
                                  )}
                                />
                              ) : (
                                <Autocomplete
                                  options={activeStains}
                                  value={stain || null}
                                  disabled={!canEdit || busy || !activeStains.length || !eligibleSlides.length}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  getOptionLabel={(option) => option.displayName || option.id}
                                  noOptionsText={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <InfoOutlinedIcon fontSize="small" color="action" />
                                      <Typography variant="caption">
                                        For a missing stain, email support@nucleaimd.com.
                                      </Typography>
                                    </Stack>
                                  }
                                  onChange={(_event, value) =>
                                    updateChannel(index, {
                                      stainTypeId: value?.id || null,
                                      channelModality: value?.dna ? 'dna' : 'mif',
                                      repeatConfirmed: false,
                                      needsModalityChoice: false,
                                    })
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      label={`Stain · channel ${index}`}
                                      helperText={
                                        !activeStains.length
                                          ? 'No available stains. Contact support@nucleaimd.com.'
                                          : !stain
                                          ? 'Choose a stain'
                                          : suggestion?.source === 'catalog-match'
                                          ? 'Matched by alias'
                                          : 'From current assignment'
                                      }
                                    />
                                  )}
                                />
                              )}
                              {channel.needsModalityChoice && (
                                <Typography variant="caption">
                                  Mixed current settings · choose RNA or a stain
                                </Typography>
                              )}
                              {includes(issues, 'Confirm this repeated stain.') && (
                                <Button
                                  color="secondary"
                                  size="small"
                                  onClick={() => updateChannel(index, { repeatConfirmed: true })}
                                  disabled={!canEdit || busy}
                                >
                                  Confirm repeat
                                </Button>
                              )}
                              {dirty &&
                                map(
                                  filter(issues, (issue) => issue !== 'Confirm this repeated stain.'),
                                  (issue) => (
                                    <Typography key={issue} variant="caption" color="error" display="block">
                                      {issue}
                                    </Typography>
                                  )
                                )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!eligibleSlides.length && (
                  <Alert severity="info">
                    No compatible slides are available in this scope. The saved panel definition is retained.
                  </Alert>
                )}
                {eligibleSlides.length > 0 && (
                  <PanelSlideDetails
                    panelId={panel.panelId}
                    slideCount={eligibleSlides.length}
                    selectedCount={selectedSlideIds.length}
                  >
                    {sourceSlideId && otherMatchingSlideCount > 0 && (
                      <Alert severity="info">
                        {otherMatchingSlideCount === 1
                          ? '1 other slide has the same channels. Select it to apply these settings too.'
                          : `${otherMatchingSlideCount} other slides have the same channels. Select them to apply these settings too.`}
                      </Alert>
                    )}
                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                      {map(metadataFilters, (split) => (
                        <TextField
                          key={split.key}
                          select
                          size="small"
                          label={split.label}
                          value={includes(split.values, splitValues[split.key]) ? splitValues[split.key] : ''}
                          disabled={busy}
                          sx={{ minWidth: 160 }}
                          onChange={(event) => {
                            setSplitValues((current) => ({ ...current, [split.key]: event.target.value }));
                            setPage(0);
                          }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {map(split.values, (value) => (
                            <MenuItem key={value} value={value}>
                              {split.key === 'biopsyType' ? startCase(value) : value}
                            </MenuItem>
                          ))}
                        </TextField>
                      ))}
                      <FormControlLabel
                        label={`Select all ${selectableFilteredSlides.length} matching slides`}
                        control={
                          <Checkbox
                            disabled={busy}
                            checked={
                              selectableFilteredSlides.length > 0 &&
                              selectableFilteredSlides.every((slide) => includes(selectedSlideIds, slide.slideId))
                            }
                            indeterminate={
                              some(selectableFilteredSlides, (slide) => includes(selectedSlideIds, slide.slideId)) &&
                              !selectableFilteredSlides.every((slide) => includes(selectedSlideIds, slide.slideId))
                            }
                            onChange={(_event, checked) =>
                              setSelectedSlideIds((current) =>
                                checked
                                  ? uniq([...current, ...map(selectableFilteredSlides, 'slideId')])
                                  : filter(current, (slideId) => !find(selectableFilteredSlides, { slideId }))
                              )
                            }
                          />
                        }
                      />
                      <Chip
                        size="small"
                        label={`${selectedSlideIds.length} ${
                          selectedSlideIds.length === 1 ? 'slide' : 'slides'
                        } selected`}
                      />
                      {selectedSlides.some((slide) => !find(filteredSlides, { slideId: slide.slideId })) && (
                        <Typography variant="caption" color="text.secondary">
                          {filter(selectedSlides, (slide) => !find(filteredSlides, { slideId: slide.slideId })).length}{' '}
                          selected outside this filter
                        </Typography>
                      )}
                      {showSlideFilter && (
                        <TextField
                          select
                          label="Slide filter"
                          size="small"
                          sx={{ minWidth: 180 }}
                          value={slideFilter}
                          onChange={(event) => {
                            setSlideFilter(event.target.value);
                            setPage(0);
                          }}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="exact">Same order</MenuItem>
                          <MenuItem value="reordered">Different order</MenuItem>
                          <MenuItem value="diverged">Edited since approval</MenuItem>
                        </TextField>
                      )}
                    </Stack>
                    <TableContainer>
                      <Table size="small" aria-label={`Slides for ${panel.panelId}`}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Select</TableCell>
                            <TableCell>Slide ID</TableCell>
                            <TableCell>Case label</TableCell>
                            <TableCell>Source file name</TableCell>
                            <TableCell>Current panel</TableCell>
                            <TableCell>Channel order</TableCell>
                            <TableCell>Approval</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {map(filteredSlides.slice(visiblePage * 10, (visiblePage + 1) * 10), (slide) => (
                            <TableRow key={slide.slideId}>
                              <TableCell>
                                <Checkbox
                                  disabled={busy || slide.match === 'unmatched'}
                                  checked={includes(selectedSlideIds, slide.slideId)}
                                  inputProps={{ 'aria-label': `Apply to ${slide.originalFileName || slide.slideId}` }}
                                  onChange={(_event, checked) =>
                                    setSelectedSlideIds((current) =>
                                      checked
                                        ? [...current, slide.slideId]
                                        : filter(current, (id) => id !== slide.slideId)
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <SlideLinkWithPreview
                                  slideid={slide.slideId}
                                  labid={labId}
                                  href={buildSlideViewerUrl({ slideId: slide.slideId, labId, studyId })}
                                >
                                  {slide.slideId}
                                </SlideLinkWithPreview>
                                {slide.slideId === sourceSlideId && (
                                  <Chip size="small" label="Current slide" sx={{ ml: 1 }} />
                                )}
                              </TableCell>
                              <TableCell>{join(slide.caseLabels || [], ', ') || 'Unassigned'}</TableCell>
                              <TableCell>{slide.originalFileName || 'Unavailable'}</TableCell>
                              <TableCell>
                                {slide.currentPanelId
                                  ? getPanelLabel(slide.currentPanelId, panels)
                                  : find(panel.slides, { slideId: slide.slideId })
                                  ? panelLabel
                                  : 'Not associated'}
                                {!find(panel.slides, { slideId: slide.slideId }) && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {Object.keys(slide.channelMapping).some(
                                      (channelId) => !slide.channelMarkerTypes[channelId]
                                    )
                                      ? 'Marker names required'
                                      : slide.currentSettingsMatch
                                      ? 'Settings match'
                                      : 'Settings differ'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {slide.match === 'unmatched' ? (
                                  'Source channels differ'
                                ) : slide.match === 'exact' ? (
                                  'Same as panel'
                                ) : (
                                  <details>
                                    <summary>Different order</summary>
                                    <Typography variant="caption">
                                      {join(
                                        map(
                                          slide.channelMapping,
                                          (panelIndex, slideIndex) => `${slideIndex} → ${panelIndex}`
                                        ),
                                        ', '
                                      )}
                                    </Typography>
                                  </details>
                                )}
                              </TableCell>
                              <TableCell>
                                {slide.approvalStatus === 'approved' ? (
                                  <>
                                    Approved by <UserIdentity userId={slide.approvedBy} usersById={usersById} />
                                    {slide.approvedAt ? ` · ${formatDateTimeForUser(slide.approvedAt)}` : ''}
                                  </>
                                ) : slide.approvalStatus === 'diverged' ? (
                                  'Edited since approval'
                                ) : (
                                  'Not approved'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={filteredSlides.length}
                      page={visiblePage}
                      rowsPerPage={10}
                      rowsPerPageOptions={[10]}
                      onPageChange={(_event, nextPage) => setPage(nextPage)}
                    />
                  </PanelSlideDetails>
                )}
                {requestError && (
                  <Alert severity="error" ref={requestErrorRef} tabIndex={-1}>
                    {requestError}
                  </Alert>
                )}
                {!canEdit && (
                  <Alert severity="info">Study editing permission is required to save or approve panels.</Alert>
                )}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {validation.invalidChannelCount ? (
                      `${validation.invalidChannelCount} ${
                        validation.invalidChannelCount === 1 ? 'channel needs' : 'channels need'
                      } attention`
                    ) : !selectedSlideIds.length ? (
                      'Select at least one slide'
                    ) : panel.savedAt ? (
                      <>
                        Saved by <UserIdentity userId={panel.savedBy} usersById={usersById} /> ·{' '}
                        {formatDateTimeForUser(panel.savedAt)}
                      </>
                    ) : (
                      'Review the suggested settings'
                    )}
                  </Typography>
                  {onDefineNewPanel && !isNewPanelDraft && eligibleSlides.length > 0 && (
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      startIcon={<AddOutlinedIcon />}
                      disabled={!canEdit || busy || !selectedSlideIds.length}
                      onClick={() => onDefineNewPanel(channels, selectedSlideIds)}
                    >
                      Define new panel for selected slides
                    </Button>
                  )}
                  {(dirty || onDiscardDraft) && (
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={busy}
                      onClick={() => {
                        if (onDiscardDraft) {
                          onDiscardDraft();
                          return;
                        }
                        setChannels(baseline);
                        setRequestError(undefined);
                        onDirtyChange(panel.panelId, false);
                      }}
                    >
                      {onDiscardDraft ? 'Cancel' : 'Discard changes'}
                    </Button>
                  )}
                  {needsSave ? (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        !canEdit ||
                        busy ||
                        validation.invalidChannelCount > 0 ||
                        !selectedSlideIds.length ||
                        (!dirty && Boolean(localRevision) && !some(selectedSlides, { approvalStatus: 'diverged' }))
                      }
                      onClick={() => save.mutate()}
                    >
                      Save panel
                    </Button>
                  ) : needsAssociation ? (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!canEdit || busy || !associationSlideIds.length}
                      onClick={applyPanel}
                    >
                      Apply panel to selected slides
                    </Button>
                  ) : selectedApproved ? (
                    <Button color="inherit" disabled={!canEdit || busy} onClick={() => approval.mutate('revoked')}>
                      Revoke approval
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<TaskAltOutlinedIcon />}
                      disabled={!canEdit || busy || !canApprove}
                      onClick={() => approval.mutate('approved')}
                    >
                      Approve {selectedSlideIds.length} {selectedSlideIds.length === 1 ? 'slide' : 'slides'}
                    </Button>
                  )}
                </Stack>
                {dirty && localRevision && (
                  <Typography variant="caption" color="text.secondary">
                    Saving a new revision supersedes previous approvals for this panel. Unchecked slides keep their
                    settings.
                    {analysisChanges
                      ? ' Channel settings changed; panel-dependent analyses need re-analysis. Initial image analysis is retained.'
                      : ' Only display names changed; re-analysis is not required.'}
                  </Typography>
                )}
                {!isDraft && (
                  <details>
                    <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>
                      Technical details
                    </Typography>
                    <Table size="small" aria-label={`Technical details for ${panel.panelId}`}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Acquisition metadata</TableCell>
                          <TableCell>Stored identifier</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {map(channels, (channel) => (
                          <TableRow key={channel.channelIndex}>
                            <TableCell>{join(channel.identifiers.sourceNames, ' / ')}</TableCell>
                            <TableCell>{validation.analysisNames[channel.channelIndex] || 'Unavailable'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </details>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Fade>
      {isRenaming && (
        <RenamePanelDialog
          studyId={studyId}
          panelId={persistedPanelId}
          currentName={panelLabel}
          onClose={() => setIsRenaming(false)}
        />
      )}
    </>
  );
};
